import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { RestResponse } from '../models/restResponse.mode';

export interface AppModule {
  id: number;
  code: string;
  name: string;
  active: boolean;
}

export interface CompanyModuleItem {
  moduleId: number;
  code: string;
  name: string;
  active: boolean;
  enabled: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ModuleAccessService {
  private readonly baseUrl = `${environment.base_url}/modules`;
  private readonly COMPANY_ID_KEY = 'company_id';
  private readonly ENABLED_MODULES_KEY = 'enabled_modules';

  constructor(private http: HttpClient) {}

  getModules(): Observable<RestResponse<AppModule[]>> {
    return this.http.get<RestResponse<AppModule[]>>(this.baseUrl);
  }

  createModule(payload: { code: string; name: string; active: boolean }): Observable<RestResponse<AppModule>> {
    return this.http.post<RestResponse<AppModule>>(`${this.baseUrl}/create`, payload);
  }

  updateModule(id: number, payload: { code: string; name: string; active: boolean }): Observable<RestResponse<AppModule>> {
    return this.http.put<RestResponse<AppModule>>(`${this.baseUrl}/update/${id}`, payload);
  }

  deleteModule(id: number): Observable<RestResponse<object>> {
    return this.http.delete<RestResponse<object>>(`${this.baseUrl}/delete/${id}`);
  }

  getCompanyModules(companyId: number): Observable<RestResponse<CompanyModuleItem[]>> {
    return this.http.get<RestResponse<CompanyModuleItem[]>>(`${this.baseUrl}/company/${companyId}`);
  }

  updateCompanyModules(companyId: number, modules: { moduleId: number; enabled: boolean }[]): Observable<RestResponse<CompanyModuleItem[]>> {
    return this.http.put<RestResponse<CompanyModuleItem[]>>(`${this.baseUrl}/company/${companyId}`, { modules });
  }

  loadEnabledModules(companyId: number): Observable<string[]> {
    return this.getCompanyModules(companyId).pipe(
      map((response) =>
        (response.data ?? [])
          .filter((item) => item.active && item.enabled)
          .map((item) => item.code)
      ),
      tap((codes) => this.setEnabledModules(codes))
    );
  }

  getLoggedCompanyId(): number {
    const raw = localStorage.getItem(this.COMPANY_ID_KEY);
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? 1 : parsed;
  }

  hasModule(code: string): boolean {
    const modules = this.getEnabledModules();
    return modules.has(code);
  }

  getEnabledModules(): Set<string> {
    const raw = localStorage.getItem(this.ENABLED_MODULES_KEY);
    if (!raw) {
      return new Set<string>();
    }

    try {
      const parsed = JSON.parse(raw) as string[];
      return new Set(parsed);
    } catch {
      return new Set<string>();
    }
  }

  setEnabledModules(codes: string[]) {
    localStorage.setItem(this.ENABLED_MODULES_KEY, JSON.stringify(codes));
  }
}
